import { createContext, useContext, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, SESSION_LOST_EVENT } from '../lib/api.js'
import { applyBranding } from '../lib/theme.js'
import { nameOf, phraseOf, enabledModes, isEnabled } from '../lib/modes.js'
import { assetsName, assetUnit, assetsOnBoard } from '../lib/assets.js'
import { listsEnabled } from '../lib/lists.js'
import { roleLabelsFor } from '../lib/roles.js'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const qc = useQueryClient()
  const { data, isLoading, error } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get('/api/me'),
    retry: false,
    staleTime: 60_000,
  })

  useEffect(() => {
    if (data?.tenant) applyBranding(data.tenant)
  }, [data])

  // Any 401 from anywhere re-asks who we are. The query fails, `signedIn` goes false, and
  // App swaps in <Login> at the URL the person was already on — so signing back in returns
  // them to the page they lost rather than to the dashboard.
  useEffect(() => {
    const onLost = () => qc.invalidateQueries({ queryKey: ['me'] })
    window.addEventListener(SESSION_LOST_EVENT, onLost)
    return () => window.removeEventListener(SESSION_LOST_EVENT, onLost)
  }, [qc])

  const value = {
    loading: isLoading,
    signedIn: Boolean(data?.user) && !error,
    user: data?.user,
    can: data?.can || {},
    tenant: data?.tenant,
    // The tenant's own vocabulary for its modes, plus the three helpers every caller wants,
    // so no page has to remember where the map came from or what to fall back to.
    modes: data?.modes,
    modeName: (mode) => nameOf(data?.modes, mode),
    modePhrase: (mode) => phraseOf(data?.modes, mode),
    enabledModes: () => enabledModes(data?.modes),
    modeEnabled: (mode) => isEnabled(data?.modes, mode),
    // Each mode admin is named after the mode it runs, so both travel with the vocabulary.
    roleLabel: (role) =>
      roleLabelsFor(nameOf(data?.modes, 'normal'), nameOf(data?.modes, 'cat'))[role] || role,
    // Asset tracking: whether this company draws its assets on the board, and what it calls
    // them ("Fleet" / "vehicle"). Same shape of helpers as the modes above, for the same reason.
    assets: data?.assets,
    assetsOnBoard: () => assetsOnBoard(data?.assets),
    assetsName: () => assetsName(data?.assets),
    assetUnit: () => assetUnit(data?.assets),
    // Lists beside the board: on or off for this company. The nav item, the settings
    // sidebar and the /lists routes all read this one answer.
    lists: data?.lists,
    listsEnabled: () => listsEnabled(data?.lists),
    // Outside applications a person may connect to their own account, and this company's own
    // connector address. Tenant configuration rather than a permission, so it sits beside
    // `assets` and `modes` rather than under `can`.
    integrations: data?.integrations,
    // Other companies the same Microsoft identity could sign into — empty for dev-signed-in
    // accounts and anyone who has never been through the tenant-choice popup.
    otherTenants: data?.otherTenants || [],
    mode: data?.mode,
    pendingSwitch: data?.pendingSwitch,
    settings: data?.settings,
    refresh: () => qc.invalidateQueries({ queryKey: ['me'] }),
    signOut: async () => {
      await api.post('/auth/logout')
      qc.clear()
      window.location.href = '/'
    },
    // A full reload rather than a query refresh: the mode, permissions and tenant branding
    // all change with the company, and a hard boot is the same simplicity a fresh sign-in has.
    switchTenant: async (tenantId) => {
      await api.post('/api/me/switch-tenant', { tenantId })
      qc.clear()
      window.location.href = '/'
    },
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => useContext(AuthContext)
